/**
 * TreeNodeRow — recursive row for TreeNavigator. Split into its own
 * module to sidestep a babel/traverse stack-overflow that fires when
 * the recursive component is co-located with its parent in JSX.
 */
import { useState, createElement } from "react";
import {
  ChevronDown, ChevronRight, Plus, Copy, Trash2, Eye, EyeOff,
  Lock, Unlock, Pencil, Check, X, GripVertical, Square, Rows, Columns, Package,
} from "lucide-react";

const NODE_ICON = {
  section: Square,
  row:     Rows,
  col:     Columns,
  widget:  Package,
};

const ADD_LABEL = {
  section: "Add Row",
  row:     "Add Column",
  col:     "Add Widget",
};

export default function TreeNodeRow(props) {
  const {
    node, depth = 0, selection, editing, setEditing, editVal, setEditVal,
    onSelect, onAddChild, onDup, onDel, onHide, onLock, onRename,
    dragId, setDragId, onReorder, allNodes,
  } = props;

  const [open, setOpen] = useState(depth < 2);
  const Icon = NODE_ICON[node.type] || Package;
  const kids = node.children || [];
  const hasChildren = kids.length > 0;
  const canAddChild = node.type !== "widget";
  const selected = selection === node.id;
  const isEditing = editing === node.id;

  const onRowDragStart = (e) => {
    setDragId(node.id);
    try { e.dataTransfer.effectAllowed = "move"; } catch { /* noop */ }
    e.stopPropagation();
  };
  const onRowDragOver = (e) => { e.preventDefault(); };
  const onRowDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!dragId || dragId === node.id) { setDragId(null); return; }
    const findSiblingIdx = (list, id) => {
      for (let i = 0; i < list.length; i += 1) {
        if (list[i].id === id) return { list, i };
        if (list[i].children) {
          const hit = findSiblingIdx(list[i].children, id);
          if (hit) return hit;
        }
      }
      return null;
    };
    const src = findSiblingIdx(allNodes, dragId);
    const dst = findSiblingIdx(allNodes, node.id);
    setDragId(null);
    if (!src || !dst || src.list !== dst.list) return;
    onReorder(dragId, dst.i);
  };

  const iconColor =
    node.type === "section" ? "text-[#E32636]" :
    node.type === "row"     ? "text-sky-600" :
    node.type === "col"     ? "text-emerald-600" : "text-slate-500";

  return (
    <div>
      <div
        className={`group flex items-center gap-1 px-2 py-1 border-l-2 transition-colors ${
          selected ? "bg-[#FFF3F4] border-l-[#E32636]" : "border-l-transparent hover:bg-white"
        } ${node.hidden ? "opacity-55" : ""} ${dragId === node.id ? "opacity-40" : ""}`}
        style={{ paddingLeft: 8 + depth * 12 }}
        draggable={!node.locked}
        onDragStart={onRowDragStart}
        onDragOver={onRowDragOver}
        onDrop={onRowDrop}
        onDragEnd={() => setDragId(null)}
        data-testid={`vb-tree-row-${node.id}`}
      >
        {hasChildren ? (
          <button
            type="button" onClick={() => setOpen(!open)}
            className="w-4 h-4 grid place-items-center text-slate-400 shrink-0"
            data-testid={`vb-tree-toggle-${node.id}`}
          >{open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}</button>
        ) : <span className="w-4" />}

        <GripVertical className={`w-3 h-3 shrink-0 cursor-grab ${node.locked ? "text-slate-200" : "text-slate-400"}`} />
        <Icon className={`w-3.5 h-3.5 shrink-0 ${iconColor}`} />

        {isEditing ? (
          <div className="flex-1 flex items-center gap-1">
            <input
              autoFocus
              value={editVal}
              onChange={(e) => setEditVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onRename(node.id);
                if (e.key === "Escape") setEditing(null);
              }}
              className="h-6 px-1.5 text-[11px] flex-1 rounded border border-[#E32636] outline-none"
              data-testid={`vb-tree-rename-input-${node.id}`}
            />
            <button type="button" onClick={() => onRename(node.id)} className="w-5 h-5 grid place-items-center rounded bg-emerald-500 text-white">
              <Check className="w-3 h-3" />
            </button>
            <button type="button" onClick={() => setEditing(null)} className="w-5 h-5 grid place-items-center rounded bg-slate-300 text-white">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button
            type="button" onClick={() => onSelect(node)}
            onDoubleClick={() => { setEditing(node.id); setEditVal(node.label || node.type); }}
            className="flex-1 min-w-0 text-left"
            data-testid={`vb-tree-select-${node.id}`}
          >
            <div className={`text-[12px] font-semibold truncate ${selected ? "text-[#E32636]" : "text-slate-800"}`}>
              {node.label || node.type}
              {node.type === "col" && <span className="ml-1 text-[9.5px] text-slate-400 font-mono">span={node.span}/12</span>}
              {node.type === "widget" && <span className="ml-1 text-[9.5px] text-slate-400 font-mono">{node.comp}</span>}
            </div>
          </button>
        )}

        <div className="hidden group-hover:flex items-center gap-0.5">
          {canAddChild && (
            <IconBtn title={ADD_LABEL[node.type]} onClick={() => onAddChild(node)} testid={`vb-tree-add-${node.id}`}>
              <Plus className="w-3 h-3" />
            </IconBtn>
          )}
          <IconBtn title="Rename" onClick={() => { setEditing(node.id); setEditVal(node.label || node.type); }} testid={`vb-tree-rename-${node.id}`}>
            <Pencil className="w-3 h-3" />
          </IconBtn>
          <IconBtn title="Duplicate" onClick={() => onDup(node.id)} testid={`vb-tree-dup-${node.id}`}>
            <Copy className="w-3 h-3" />
          </IconBtn>
          <IconBtn title="Delete" onClick={() => onDel(node.id)} danger testid={`vb-tree-del-${node.id}`}>
            <Trash2 className="w-3 h-3" />
          </IconBtn>
        </div>

        <IconBtn
          title={node.locked ? "Unlock" : "Lock"}
          onClick={() => onLock(node.id, node.locked)}
          testid={`vb-tree-lock-${node.id}`} active={node.locked}
        >{node.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}</IconBtn>
        <IconBtn
          title={node.hidden ? "Show" : "Hide"}
          onClick={() => onHide(node.id, node.hidden)}
          testid={`vb-tree-vis-${node.id}`} active={node.hidden}
        >{node.hidden ? <EyeOff className="w-3 h-3 text-red-500" /> : <Eye className="w-3 h-3" />}</IconBtn>
      </div>

      {open && hasChildren && (
        <div>
          {kids.map((child) => createElement(TreeNodeRow, {
            key: child.id,
            node: child,
            depth: depth + 1,
            selection,
            editing, setEditing,
            editVal, setEditVal,
            onSelect,
            onAddChild,
            onDup, onDel, onHide, onLock, onRename,
            dragId, setDragId,
            onReorder,
            allNodes,
          }))}
        </div>
      )}
    </div>
  );
}

function IconBtn({ children, title, onClick, testid, danger, active }) {
  return (
    <button
      type="button" title={title} onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      data-testid={testid}
      className={`w-5 h-5 grid place-items-center rounded ${
        danger ? "text-red-500 hover:bg-red-50" :
        active ? "text-[#E32636] bg-[#FFF3F4]" :
                 "text-slate-500 hover:bg-slate-100"
      }`}
    >{children}</button>
  );
}
